import React, { useEffect, useState } from 'react';
import { useUser } from '../hooks/useUser';
import classNames from '../utility/classNames';
import { analytics, functions } from '../../firebase/clientApp';
import { httpsCallable } from 'firebase/functions';
import { logEvent } from 'firebase/analytics';
import validateEmailSyntax from '../utility/validateEmailSyntax';
import ConfirmModal from '../views/PropertiesPage/ConfirmModal';
import toast from 'react-hot-toast';

export default function FeedbackModal({ showPrompt, setShowPrompt }) {
  const { user, userDocData } = useUser();
  const { email, userName, displayName, uid } = userDocData || {};
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onFeedback = httpsCallable(functions, 'onFeedbackReceived');

  const [feedback, setFeedback] = useState({
    name: userName || displayName || '',
    email: email || '',
    message: '',
    uid: uid || '',
    isUser: !!user,
  });

  useEffect(() => {
    setFeedback({
      ...feedback,
      name: userName || displayName || '',
      email: email || '',
      uid: uid || '',
      isUser: !!user,
    });
  }, [user]);

  const handleSubmit = async () => {
    if (!feedback.message) {
      setError('Please enter a message');
      return false;
    }
    if (!validateEmailSyntax(feedback.email, setError)) {
      return false;
    }

    try {
      await onFeedback(feedback);
      logEvent(analytics, 'feedback');
      setSent(true);
      setError(null);
      toast.success('Feedback sent successfully', { position: 'top-right' });
    } catch (err) {
      logEvent(analytics, 'exception', {
        description: err.code,
        content_type: 'feedback',
      });
      setError('Something went wrong. Please try again later.');
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({ ...feedback, [name]: value });
  };

  const inputClasses =
    'max-w-xl block w-full border-transparent border focus:border-primary focus:ring-transparent  text-md font-normal text-neutral-900 bg-neutral-input rounded py-0 px-3';

  const modalContent = (
    <div className="flex flex-col items-center w-full mx-auto max-w-md sm:max-w-xl overflow-hidden text-left align-middle transition-all transform ">
      <div className="flex flex-col items-center justify-between">
        {!sent && (
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col gap-4 w-full items-center text-md font-normal tracking-wider text-neutral-900"
          >
            <div className="w-full flex flex-col items-center gap-1">
              <label htmlFor="name">Name</label>
              <input
                className={inputClasses}
                type="text"
                name="name"
                id="name"
                value={feedback.name}
                onChange={handleChange}
              />
            </div>
            <div className="w-full flex flex-col items-center gap-1">
              <label htmlFor="email">Email</label>
              <input
                className={classNames(inputClasses, email && 'bg-gray-100')}
                type="email"
                name="email"
                id="email"
                value={feedback.email}
                onChange={handleChange}
                disabled={email}
              />
            </div>
            <div className="w-full flex flex-col h-36 items-center gap-1">
              <label htmlFor="message">Message</label>
              <textarea
                className={classNames(inputClasses, 'min-h-24 p-4')}
                rows="10"
                cols="50"
                name="message"
                id="message"
                value={feedback.message}
                onChange={handleChange}
              />
            </div>
          </form>
        )}
      </div>
      {error && (
        <div className="flex flex-col items-center gap-3 mt-4 w-full">
          <p className="text-warning text-md">{error}</p>
        </div>
      )}
    </div>
  );

  return (
    <ConfirmModal
      title={'Share Feedback'}
      subtitle={'Share your thoughts with us, we would love to hear from you!'}
      showPrompt={showPrompt}
      setShowPrompt={setShowPrompt}
      description={modalContent}
      confirmText={'Send'}
      onConfirm={handleSubmit}
      blockCloseIfFalse={true}
    />
  );
}
